<template>
  <div class="mt-4">
    <div class="flex items-center justify-between mb-3">
      <h2 class="form-subtitle mb-0">Plantillas de contrato</h2>
    </div>

    <table class="wz-table w-full">
      <thead>
        <tr>
          <th>Plantilla</th>
          <th>Estado</th>
          <th class="text-right">Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="rows.length === 0">
          <td colspan="3" class="text-center text-muted">No hay plantillas aplicables a este tipo de contrato.</td>
        </tr>
        <tr v-for="r in rows" :key="r.id">
          <td>{{ r.name }}</td>
          <td>
            <template v-if="r.variant">
              <wz-badge variant="success" pill>✨ Personalizada</wz-badge>
              <small class="text-muted block mt-1">
                {{ formatDate(r.variant.updatedAt || r.variant.createdAt) }}
                <span v-if="r.variant.updatedBy || r.variant.createdBy"> · {{ r.variant.updatedBy || r.variant.createdBy }}</span>
              </small>
            </template>
            <span v-else class="text-muted">Usa plantilla base</span>
          </td>
          <td class="text-right">
            <template v-if="r.variant">
              <wz-button variant="outline" size="sm" class="mr-2" @click="downloadVariant(r.variant)">Ver DOCX</wz-button>
              <wz-button variant="outline" size="sm" class="mr-2" @click="openCustomize(r)">Reemplazar</wz-button>
              <wz-button variant="danger" size="sm" @click="openDelete(r)">Eliminar personalización</wz-button>
            </template>
            <wz-button v-else variant="primary" size="sm" @click="openCustomize(r)">Personalizar plantilla</wz-button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modal: Personalizar / Reemplazar -->
    <wz-modal v-model="modalCustomize" :title="customizeRow ? 'Personalizar: ' + customizeRow.name : 'Personalizar plantilla'" size="lg">
      <div class="mb-4">
        <p class="text-sm text-muted mb-2">
          <strong>Paso 1.</strong> Descargue la plantilla base para editarla y usarla como punto de partida.
        </p>
        <wz-button variant="outline-secondary" size="sm" :disabled="downloadingBase" @click="downloadBase">
          {{ downloadingBase ? 'Descargando...' : 'Descargar plantilla base' }}
        </wz-button>
      </div>
      <div>
        <p class="text-sm text-muted mb-2">
          <strong>Paso 2.</strong> Suba el DOCX personalizado para este colaborador (mantenga los campos <code>${...}</code>).
        </p>
        <input type="file" accept=".docx" class="mb-1" @change="onCustomizeFile" />
        <div v-if="customizeFile" class="text-sm mb-3">Archivo: <strong>{{ customizeFile.name }}</strong></div>
        <wz-textarea label="Notas (opcional)" v-model="customizeNotes" rows="2" :maxlength="500" placeholder="Ej.: cláusula de confidencialidad adicional" />
        <div class="text-sm text-muted text-right mt-1">{{ (customizeNotes || '').length }} / 500 caracteres</div>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalCustomize = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!customizeFile || saving" @click="submitCustomize">
          {{ saving ? 'Guardando...' : 'Guardar' }}
        </wz-button>
      </template>
    </wz-modal>

    <!-- Modal: confirmar reemplazo (si ya existe variante) -->
    <wz-modal v-model="modalReplaceConfirm" title="Reemplazar personalización" size="sm">
      <p>Ya existe una personalización para esta plantilla. ¿Desea reemplazarla con el nuevo archivo?</p>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalReplaceConfirm = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="saving" @click="doSave">
          {{ saving ? 'Guardando...' : 'Reemplazar' }}
        </wz-button>
      </template>
    </wz-modal>

    <!-- Modal: eliminar personalización -->
    <wz-modal v-model="modalDelete" title="Eliminar personalización" size="sm">
      <p>¿Eliminar la personalización de esta plantilla? El colaborador volverá a usar la plantilla base.</p>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalDelete = false">Cancelar</wz-button>
        <wz-button variant="danger" :disabled="deleting" @click="doDelete">
          {{ deleting ? 'Eliminando...' : 'Eliminar' }}
        </wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert, Util } from '@wizepe/wizeui'
import fileDownload from 'js-file-download'
import { ContractsService } from '@/core/services'
import ContractTemplateService from '@/core/services/ContractTemplateService'
import EmployeeContractTemplateVariantService from '@/core/services/EmployeeContractTemplateVariantService'

export default {
  name: 'ContractTemplateVariantsSection',
  props: {
    contractId: { type: [Number, String], required: true },
    employeeId: { type: [Number, String], required: true },
  },
  data() {
    return {
      templates: [],
      variantsByTemplate: {},
      modalCustomize: false,
      customizeRow: null,
      customizeFile: null,
      customizeNotes: '',
      saving: false,
      downloadingBase: false,
      modalReplaceConfirm: false,
      modalDelete: false,
      deleteRow: null,
      deleting: false,
    }
  },
  computed: {
    // Cada plantilla aplicable, enriquecida con su variante (si el colaborador la tiene).
    rows() {
      return this.templates.map(t => ({ ...t, variant: this.variantsByTemplate[t.id] || null }))
    },
  },
  created() {
    this.load()
  },
  methods: {
    formatDate(d) {
      if (!d) return '—'
      const iso = String(d).substring(0, 10)
      const parts = iso.split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : iso
    },
    async load() {
      try {
        const [tplResp, varResp] = await Promise.all([
          ContractsService.applicableTemplates(this.contractId),
          EmployeeContractTemplateVariantService.list(this.employeeId),
        ])
        this.templates = tplResp.payload || []
        const map = {}
        ;(varResp.payload || []).forEach(v => { map[v.templateId] = v })
        this.variantsByTemplate = map
      } catch (e) {
        Alert.error('Error al cargar las plantillas del contrato')
      }
    },
    openCustomize(row) {
      this.customizeRow = row
      this.customizeFile = null
      this.customizeNotes = row.variant && row.variant.notes ? row.variant.notes : ''
      this.modalCustomize = true
    },
    onCustomizeFile(event) {
      const file = event.target.files && event.target.files[0]
      if (file && !file.name.toLowerCase().endsWith('.docx')) {
        Alert.error('El archivo debe ser un .docx')
        event.target.value = ''
        this.customizeFile = null
        return
      }
      this.customizeFile = file || null
    },
    async downloadBase() {
      if (!this.customizeRow) return
      try {
        this.downloadingBase = true
        const resp = await ContractTemplateService.downloadFile(this.customizeRow.id)
        fileDownload(resp.data, Util.getFileName(resp) || (this.customizeRow.name + '.docx'))
      } catch (e) {
        Alert.error('Error al descargar la plantilla base')
      } finally {
        this.downloadingBase = false
      }
    },
    // Si la plantilla ya tiene variante → confirmar reemplazo; si no → guardar directo.
    submitCustomize() {
      if (!this.customizeFile) return
      if (this.customizeRow && this.customizeRow.variant) {
        this.modalReplaceConfirm = true
      } else {
        this.doSave()
      }
    },
    async doSave() {
      if (!this.customizeRow || !this.customizeFile) return
      try {
        this.saving = true
        const resp = await EmployeeContractTemplateVariantService.createOrReplace(this.employeeId, {
          templateId: this.customizeRow.id,
          file: this.customizeFile,
          notes: this.customizeNotes || null,
        })
        Alert.success((resp.payload && resp.payload.message) || 'Personalización guardada')
        // Riesgo 2: si la variante omite placeholders de la base, el backend devuelve un warning.
        if (resp.payload && resp.payload.warning) Alert.alert('Advertencia', resp.payload.warning)
        this.modalReplaceConfirm = false
        this.modalCustomize = false
        await this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar la personalización')
      } finally {
        this.saving = false
      }
    },
    async downloadVariant(variant) {
      try {
        const resp = await EmployeeContractTemplateVariantService.download(this.employeeId, variant.id)
        fileDownload(resp.data, Util.getFileName(resp) || variant.documentFileName || 'variante.docx')
      } catch (e) {
        Alert.error('Error al descargar el DOCX personalizado')
      }
    },
    openDelete(row) {
      this.deleteRow = row
      this.modalDelete = true
    },
    async doDelete() {
      if (!this.deleteRow || !this.deleteRow.variant) return
      try {
        this.deleting = true
        await EmployeeContractTemplateVariantService.remove(this.employeeId, this.deleteRow.variant.id)
        Alert.success('Personalización eliminada')
        this.modalDelete = false
        await this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al eliminar la personalización')
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style lang="stylus" scoped></style>
